// components/chatbot/chatbot.component.ts
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import {
  ApiService,
  ChatMessage,
  ChatHistory,
  Citation,
  DisambiguationOption,
  PolicyDetail,
} from '../../services/api.service';
import { AzureAuthService } from '../../services/azure-auth.service';
import { Subscription } from 'rxjs';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('textareaRef') textareaRef!: ElementRef;

  chatHistory: ChatHistory = { messages: [] };
  currentMessage = '';
  isLoading = false;
  sqlVisible: boolean[] = [];
  chartVisible: boolean[] = [];
  viewMode: ('table' | 'chart')[] = [];
  contextVisible: boolean[] = [];
  citationsExpanded: boolean[] = []; // Para expandir/colapsar citaciones

  private chatSubscription: Subscription | null = null;
  private shouldScrollToBottom = false;
  private charts: Chart[] = [];

  // 🌍 Detección automática de región via Graph API
  private userUsageLocation: string | null = null;

  // 🌍 Configuración de Selector de Región
  showRegionSelector = false;
  availableRegions = [
    { code: 'MX', label: 'Mexico (MX)', flag: '🇲🇽' },
    { code: 'BR', label: 'Brasil (BR)', flag: '🇧🇷' },
    { code: 'USA', label: 'Estados Unidos (USA)', flag: '🇺🇸' },
    { code: 'ELO', label: 'ELOPAK (ELO)', flag: '🏢' },
    { code: 'GLOBAL', label: 'Todas las regiones', flag: '🌐' },
  ];

  // 🚀 PREGUNTAS SUGERIDAS - Políticas Corporativas Lala
  suggestedQuestions = [
    '¿Quiénes son elegibles para el programa de autos de prestación?',
    '¿Cuál es el proceso para reportar una denuncia al Comité de Ética?',
    '¿Qué dice la Política Anticorrupción sobre regalos de proveedores?',
    '¿Cómo solicito descuentos como colaborador?',
    // '¿Cuáles son los lineamientos de la Política de Frescura?',
    // '¿Cómo se administran las licencias de usuarios SAP?',
    // '¿Qué establece el Código de Ética sobre conflictos de interés?',
    // '¿Cuál es el procedimiento para dar de baja un activo fijo?'
  ];

  // 👤 Menú de usuario
  showUserMenu = false;

  constructor(
    private apiService: ApiService,
    private azureAuthService: AzureAuthService,
  ) {}

  // 👤 Métodos para el menú de usuario
  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  closeUserMenu(): void {
    this.showUserMenu = false;
  }

  async onSignOut(): Promise<void> {
    this.showUserMenu = false;
    await this.azureAuthService.signOut();
  }

  getUserEmail(): string {
    const user = this.azureAuthService.currentUserValue;
    return user?.email || user?.username || 'Usuario';
  }

  getUserInitials(): string {
    const email = this.getUserEmail();
    return email.charAt(0).toUpperCase();
  }

  ngOnInit() {
    this.chatSubscription = this.apiService.chatHistory$.subscribe(
      (history) => {
        this.chatHistory = history;
        this.shouldScrollToBottom = true;
        // Inicializar arrays de visibilidad
        this.sqlVisible = new Array(this.chatHistory.messages.length).fill(
          false,
        );
        this.chartVisible = new Array(this.chatHistory.messages.length).fill(
          false,
        );
        this.viewMode = new Array(this.chatHistory.messages.length).fill(
          'table',
        );
        this.contextVisible = new Array(this.chatHistory.messages.length).fill(
          false,
        );
        this.citationsExpanded = new Array(
          this.chatHistory.messages.length,
        ).fill(false);
      },
    );

    // 🌍 Obtener ubicación del usuario desde Graph API para detección automática de región
    this.loadUserUsageLocation();
  }

  /**
   * Obtiene la ubicación del usuario desde Microsoft Graph API
   * para pre-seleccionar automáticamente la región del bot
   */
  private async loadUserUsageLocation(): Promise<void> {
    try {
      // 1. Intentar obtener de localStorage primero (si se pre-cargó en el login)
      const storedLocation = localStorage.getItem('user_usage_location');
      if (storedLocation) {
        // Parsear JSON porque localStorageService.saveData() hace JSON.stringify()
        try {
          this.userUsageLocation = JSON.parse(storedLocation);
        } catch {
          // Si no es JSON válido, usar el valor directamente
          this.userUsageLocation = storedLocation;
        }
        console.log(
          '📍 Ubicación del usuario (desde caché):',
          this.userUsageLocation,
        );
        return;
      }

      // 2. Si no está en caché, consultar Graph API
      console.log('📍 Obteniendo ubicación de Graph API...');
      const location = await this.azureAuthService.getUsageLocation();

      if (location) {
        this.userUsageLocation = location;
        localStorage.setItem('user_usage_location', location);
        console.log('📍 Ubicación obtenida y guardada:', location);
      } else {
        console.warn(
          '⚠️ No se pudo obtener usageLocation - se mostrará selector de región si el backend lo requiere',
        );
      }
    } catch (error) {
      console.error('❌ Error crítico obteniendo ubicación:', error);
    }
  }

  ngOnDestroy() {
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    // Limpiar gráficos
    this.charts.forEach((chart) => chart.destroy());
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom() {
    if (this.messagesContainer) {
      const container = this.messagesContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  sendMessage() {
    if (!this.currentMessage.trim() || this.isLoading) return;

    const message = this.currentMessage.trim();
    this.currentMessage = '';
    this.isLoading = true;

    // Reset textarea height after clearing the message
    this.resetTextareaHeight();

    console.log('💬 Enviando mensaje:', message);

    // Agregar mensaje del usuario al historial
    const userMessage: ChatMessage = {
      user_message: message,
      timestamp: Date.now(),
    };

    this.apiService.addMessageToHistory(userMessage);
    console.log('✅ Mensaje del usuario agregado al historial');

    // Determinar si enviar usageLocation
    // Seguir enviando hasta que se confirme que el backend asignó región (no muestra selector)
    const locationToSend = this.userUsageLocation || undefined;
    if (locationToSend) {
      console.log('📍 Enviando usageLocation en mensaje:', locationToSend);
    } else {
      console.warn('⚠️ No hay usageLocation disponible aún');
    }

    // Enviar mensaje al API RAG de políticas
    console.log(
      '📡 Llamando a sendPolicyQuestion con usage_location:',
      locationToSend,
    );
    this.apiService
      .sendPolicyQuestion(message, locationToSend || undefined)
      .subscribe({
        next: (response) => {
          console.log('✅ Respuesta RAG recibida:', response);
          this.handleRAGResponse(response);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('💥 Error enviando mensaje:', error);
          this.handleApiError(error);
          this.isLoading = false;
        },
        complete: () => {
          console.log('✅ Llamada al API completada');
          this.isLoading = false;
        },
      });
  }

  sendSuggestedQuestion(question: string) {
    this.currentMessage = question;
    this.sendMessage();
  }

  /**
   * ADR-016: Manejar selección de opción de desambiguación.
   * Envía la selección al backend para que procese la pregunta original con el filtro.
   */
  selectDisambiguationOption(option: DisambiguationOption) {
    console.log('🔀 Selección de política:', option);
    this.isLoading = true;

    // Enviar selección al backend
    this.apiService.sendPolicySelection(option.policy_key).subscribe({
      next: (response) => {
        console.log('✅ Respuesta de selección de política:', response);
        this.handleRAGResponse(response);
      },
      error: (error) => {
        console.error('❌ Error en selección de política:', error);
        this.handleApiError(error);
      },
    });
  }

  // 🌍 Método para procesar la selección de región
  onRegionSelected(regionCode: string) {
    console.log('🌍 Región seleccionada:', regionCode);
    this.showRegionSelector = false;
    this.isLoading = true; // Mostrar carga nuevamente

    this.apiService.sendRegionSelection(regionCode).subscribe({
      next: (response) => {
        console.log('✅ Selección de región enviada exitosamente');
        this.handleRAGResponse(response); // Procesar la respuesta (que puede ser la respuesta a la pregunta original)
      },
      error: (error) => {
        console.error('❌ Error enviando selección de región:', error);
        this.handleApiError(error);
        this.isLoading = false;
      },
    });
  }

  // 🚀 NUEVO: Manejar respuesta RAG de políticas
  private handleRAGResponse(response: any) {
    console.log('🔄 Procesando respuesta RAG:', response);

    if (!response) {
      console.error('❌ Respuesta vacía del API');
      this.handleApiError('Respuesta vacía del servidor');
      return;
    }

    // 🌍 Detección de solicitud de región
    if (
      response.action === 'show_card' &&
      (response.card_type === 'region_selection' ||
        response.card?.body?.some((el: any) => el.id === 'region_selection'))
    ) {
      console.log('🌍 Solicitud de selección de región recibida');
      this.showRegionSelector = true;
      this.isLoading = false; // Detener carga mientras usuario selecciona
      return;
    }

    const currentHistory = this.apiService.getCurrentHistory();
    const lastMessage =
      currentHistory.messages[currentHistory.messages.length - 1];

    if (lastMessage) {
      console.log('📝 Actualizando último mensaje con respuesta RAG');

      const updatedMessage: ChatMessage = {
        ...lastMessage,
        answer: response.answer || undefined,
        citations: response.citations || [],
        relevance_warning: response.relevance_warning || undefined,
        suggested_queries: response.suggested_queries || [],
        // ADR-016: Opciones de desambiguación
        disambiguation_options: response.disambiguation_options || undefined,
        policy_details: response.policy_details || undefined,
        usage_hint: response.usage_hint || undefined,
        success: true,
        error_details: undefined,
      };

      console.log('✅ Mensaje RAG actualizado:', updatedMessage);

      const updatedHistory = {
        messages: [...currentHistory.messages.slice(0, -1), updatedMessage],
      };

      this.apiService.updateChatHistory(updatedHistory);
      console.log('✅ Historial actualizado exitosamente');
    } else {
      console.error('❌ No se encontró el último mensaje para actualizar');
    }
    this.isLoading = false;
  }

  // Legacy: Manejar respuesta del API SQL (mantener para compatibilidad)
  private handleApiResponse(response: any) {
    console.log('🔄 Procesando respuesta del API:', response);

    if (!response) {
      console.error('❌ Respuesta vacía del API');
      this.handleApiError('Respuesta vacía del servidor');
      return;
    }

    const currentHistory = this.apiService.getCurrentHistory();
    const lastMessage =
      currentHistory.messages[currentHistory.messages.length - 1];

    if (lastMessage) {
      console.log('📝 Actualizando último mensaje con respuesta');

      const updatedMessage: ChatMessage = {
        ...lastMessage,
        sql_query: response.sql_query || undefined,
        query_results: response.query_results || null,
        interpretation: response.interpretation || undefined,
        needs_clarification: response.needs_clarification || false,
        clarification_suggestions:
          response.clarification_suggestions || undefined,
        suggested_queries: response.suggested_queries || undefined,
        query_analysis: response.query_analysis || undefined,
        context_applied: response.context_applied || undefined,
        success: response.success !== undefined ? response.success : true,
        error_details: response.error_details || undefined,
      };

      console.log('✅ Mensaje actualizado:', updatedMessage);

      const updatedHistory = {
        messages: [...currentHistory.messages.slice(0, -1), updatedMessage],
      };

      this.apiService.updateChatHistory(updatedHistory);
      console.log('✅ Historial actualizado exitosamente');
    } else {
      console.error('❌ No se encontró el último mensaje para actualizar');
    }
    this.isLoading = false;
  }

  private handleApiError(error: any) {
    console.error('💥 Manejando error del API:', error);

    // Actualizar el último mensaje con error en lugar de agregar uno nuevo
    const currentHistory = this.apiService.getCurrentHistory();
    const lastMessage =
      currentHistory.messages[currentHistory.messages.length - 1];

    if (lastMessage) {
      const errorMessage: ChatMessage = {
        ...lastMessage,
        interpretation:
          'Lo siento, ocurrió un error al procesar tu consulta. Por favor intenta nuevamente.',
        query_results: null,
        sql_query: undefined,
        success: false,
        error_details:
          typeof error === 'string'
            ? error
            : error?.message || 'Error desconocido',
      };

      console.log('📝 Actualizando último mensaje con error:', errorMessage);

      const updatedHistory = {
        messages: [...currentHistory.messages.slice(0, -1), errorMessage],
      };

      this.apiService.updateChatHistory(updatedHistory);
    }
    this.isLoading = false;
  }

  toggleSqlVisibility(index: number) {
    this.sqlVisible[index] = !this.sqlVisible[index];
  }

  // 🚀 NUEVO: Toggle para mostrar contexto aplicado
  toggleContextVisibility(index: number) {
    this.contextVisible[index] = !this.contextVisible[index];
  }

  toggleViewMode(index: number) {
    this.viewMode[index] = this.viewMode[index] === 'table' ? 'chart' : 'table';

    if (this.viewMode[index] === 'chart') {
      // Crear gráfico después de que el DOM se actualice
      setTimeout(() => {
        this.createChart(index);
      }, 100);
    }
  }

  createChart(messageIndex: number) {
    const message = this.chatHistory.messages[messageIndex];
    if (!message.query_results || !message.query_results.data) return;

    const canvasId = `chart-${messageIndex}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    // Destruir gráfico existente si existe
    if (this.charts[messageIndex]) {
      this.charts[messageIndex].destroy();
    }

    const chartConfig = this.generateChartConfig(
      message.query_results,
      message.query_analysis,
    );

    this.charts[messageIndex] = new Chart(canvas, chartConfig);
  }

  // 🚀 MEJORADO: Incluir análisis de consulta para mejor detección de tipo de gráfico
  private generateChartConfig(
    queryResults: any,
    queryAnalysis?: any,
  ): ChartConfiguration {
    const data = queryResults.data;
    const columns = queryResults.columns;

    // Usar análisis de consulta para mejor detección de tipo
    const chartType = this.detectChartType(data, columns, queryAnalysis);

    // Detectar las mejores columnas para usar
    const columnMapping = this.detectBestColumns(data, columns);

    if (chartType === 'pie') {
      return this.createPieChart(data, columns, columnMapping);
    } else if (chartType === 'line') {
      return this.createLineChart(data, columns, columnMapping);
    } else {
      return this.createBarChart(data, columns, columnMapping);
    }
  }

  private detectBestColumns(
    data: any[],
    columns: string[],
  ): {
    labelColumn: string;
    valueColumn: string;
    additionalColumns?: string[];
  } {
    // Analizar cada columna para determinar si es numérica y su relevancia
    const columnAnalysis = columns.map((col) => {
      const numericValues = data.filter(
        (row) =>
          typeof row[col] === 'number' ||
          (!isNaN(Number(row[col])) && Number(row[col]) > 0),
      ).length;

      const isNumeric = numericValues > data.length * 0.5; // Al menos 50% de valores numéricos válidos
      const weight = this.getColumnWeight(col);
      const avgValue = isNumeric
        ? data.reduce((sum, row) => sum + (Number(row[col]) || 0), 0) /
          data.length
        : 0;

      return {
        name: col,
        isNumeric,
        weight,
        avgValue,
        numericValues,
        relevanceScore: isNumeric ? weight + (avgValue > 100 ? 2 : 0) : 0,
      };
    });

    console.log('🔍 Column analysis:', columnAnalysis);

    // Ordenar columnas numéricas por relevancia
    const numericColumns = columnAnalysis
      .filter((col) => col.isNumeric)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    // Columnas categóricas (no numéricas)
    const categoricalColumns = columnAnalysis
      .filter((col) => !col.isNumeric)
      .sort((a, b) => a.name.length - b.name.length); // Preferir nombres cortos para etiquetas

    // Seleccionar la mejor columna numérica
    const valueColumn =
      numericColumns.length > 0
        ? numericColumns[0].name
        : columns[columns.length - 1];

    // Seleccionar la mejor columna categórica para etiquetas
    const labelColumn =
      categoricalColumns.length > 0
        ? categoricalColumns[0].name
        : columns.find((col) => col !== valueColumn) || columns[0];

    // Columnas adicionales para gráficos multi-serie (máximo 2 adicionales)
    const additionalColumns = numericColumns.slice(1, 3).map((col) => col.name);

    console.log('🎯 Final column mapping:', {
      labelColumn,
      valueColumn,
      additionalColumns,
      totalRecords: data.length,
    });

    return {
      labelColumn,
      valueColumn,
      additionalColumns,
    };
  }

  private getColumnWeight(columnName: string): number {
    const name = columnName.toLowerCase();
    let weight = 0;

    // 🚀 PESOS MEJORADOS para las nuevas consultas del Lambda
    if (name.includes('total') || name.includes('sum')) weight += 12;
    if (name.includes('ventas') || name.includes('sales')) weight += 11;
    if (name.includes('crecimiento') || name.includes('growth')) weight += 10;
    if (name.includes('participacion') || name.includes('share')) weight += 10;
    if (name.includes('ranking') || name.includes('rank')) weight += 9;
    if (
      name.includes('cantidad') ||
      name.includes('count') ||
      name.includes('qty')
    )
      weight += 8;
    if (name.includes('volumen') || name.includes('volume')) weight += 8;
    if (
      name.includes('porcentaje') ||
      name.includes('pct') ||
      name.includes('%')
    )
      weight += 7;
    if (
      name.includes('precio') ||
      name.includes('price') ||
      name.includes('cost')
    )
      weight += 6;
    if (name.includes('unidades') || name.includes('units')) weight += 8;
    if (
      name.includes('promedio') ||
      name.includes('average') ||
      name.includes('avg')
    )
      weight += 6;
    if (name.includes('diferencia') || name.includes('diff')) weight += 5;

    // Bonus para análisis específicos
    if (name.includes('anterior') || name.includes('previous')) weight += 3;
    if (
      name.includes('maxima') ||
      name.includes('maximum') ||
      name.includes('max')
    )
      weight += 4;
    if (
      name.includes('minima') ||
      name.includes('minimum') ||
      name.includes('min')
    )
      weight += 4;

    return weight;
  }

  // 🚀 MEJORADO: Incluir análisis de consulta para mejor detección
  private detectChartType(
    data: any[],
    columns: string[],
    queryAnalysis?: any,
  ): ChartType {
    // Usar el análisis de consulta del Lambda para mejor detección
    if (queryAnalysis?.query_type) {
      const queryType = queryAnalysis.query_type;

      console.log('🔍 Query type from analysis:', queryType);

      // Mapear tipos de consulta a tipos de gráfico
      switch (queryType) {
        case 'historical_trend':
        case 'growth_analysis':
          return 'line';
        case 'market_share':
        case 'segment_analysis':
          return data.length <= 12 ? 'pie' : 'bar';
        case 'ranking':
          return 'bar';
        case 'comparative':
          return data.length <= 8 ? 'pie' : 'bar';
        default:
          break;
      }
    }

    // Detectar columnas numéricas
    const numericColumns = columns.filter((col) =>
      data.some(
        (row) =>
          typeof row[col] === 'number' ||
          (!isNaN(Number(row[col])) && Number(row[col]) > 0),
      ),
    );

    console.log(
      '🔍 Detecting chart type. Columns:',
      columns,
      'Numeric columns:',
      numericColumns,
    );

    // Detectar patrones específicos en nombres de columnas
    const specialCases = this.detectSpecialCases(columns, data);
    if (specialCases) {
      console.log('📊 Special case detected:', specialCases);
      return specialCases;
    }

    // Lógica de detección existente...
    if (data.length > 30) {
      return 'bar';
    }

    if (data.length > 15) {
      return 'bar';
    }

    if (numericColumns.length >= 1 && data.length <= 15) {
      return 'pie';
    }

    return 'bar';
  }

  private detectSpecialCases(columns: string[], data: any[]): ChartType | null {
    const columnNames = columns.map((col) => col.toLowerCase());

    // 🚀 CASOS ESPECIALES MEJORADOS para las nuevas consultas

    // Análisis de crecimiento y tendencias → líneas
    if (
      columnNames.some(
        (col) =>
          col.includes('crecimiento') ||
          col.includes('growth') ||
          col.includes('anterior') ||
          col.includes('previous') ||
          col.includes('diferencia') ||
          col.includes('diff') ||
          col.includes('año') ||
          col.includes('year') ||
          col.includes('mes') ||
          col.includes('month') ||
          col.includes('trimestre') ||
          col.includes('quarter'),
      )
    ) {
      return 'line';
    }

    // Participación, rankings, porcentajes → pie para pocos datos
    if (
      columnNames.some(
        (col) =>
          col.includes('participacion') ||
          col.includes('porcentaje') ||
          col.includes('ranking') ||
          col.includes('rank') ||
          col.includes('share') ||
          col.includes('pct') ||
          col.includes('%'),
      ) &&
      data.length <= 12
    ) {
      return 'pie';
    }

    // Comparativas por marca/modelo
    if (
      columnNames.some(
        (col) =>
          col.includes('marca') ||
          col.includes('make') ||
          col.includes('modelo') ||
          col.includes('model'),
      ) &&
      data.length <= 10
    ) {
      return 'pie';
    }

    return null;
  }

  private createBarChart(
    data: any[],
    columns: string[],
    columnMapping: {
      labelColumn: string;
      valueColumn: string;
      additionalColumns?: string[];
    },
  ): ChartConfiguration {
    const { labelColumn, valueColumn, additionalColumns } = columnMapping;

    console.log(
      '📊 Bar Chart - Label Column:',
      labelColumn,
      'Value Column:',
      valueColumn,
    );

    // Para datasets grandes, mostrar solo los top N registros
    let chartData = data;
    let showingTopN = false;

    if (data.length > 50) {
      chartData = data
        .filter((row) => (Number(row[valueColumn]) || 0) > 0)
        .sort(
          (a, b) =>
            (Number(b[valueColumn]) || 0) - (Number(a[valueColumn]) || 0),
        )
        .slice(0, 20);
      showingTopN = true;
    } else if (data.length > 25) {
      chartData = data
        .filter((row) => (Number(row[valueColumn]) || 0) > 0)
        .sort(
          (a, b) =>
            (Number(b[valueColumn]) || 0) - (Number(a[valueColumn]) || 0),
        )
        .slice(0, 25);
      showingTopN = true;
    }

    if (chartData.length === 0) {
      chartData = data.slice(0, 10);
    }

    // Crear datasets principal
    const datasets = [
      {
        label: showingTopN
          ? `Top ${chartData.length} - ${valueColumn}`
          : valueColumn,
        data: chartData.map((row) => Number(row[valueColumn]) || 0),
        backgroundColor: 'rgba(195, 0, 47, 0.8)', // Lala Red
        borderColor: 'rgba(195, 0, 47, 1)',
        borderWidth: 1,
      },
    ];

    // Agregar datasets adicionales si existen múltiples columnas numéricas
    if (additionalColumns && additionalColumns.length > 0) {
      const additionalColors = [
        'rgba(59, 130, 246, 0.8)', // Blue
        'rgba(16, 185, 129, 0.8)', // Green
      ];

      additionalColumns.forEach((col, index) => {
        datasets.push({
          label: col,
          data: chartData.map((row) => Number(row[col]) || 0),
          backgroundColor:
            additionalColors[index] || 'rgba(107, 114, 128, 0.8)',
          borderColor:
            additionalColors[index]?.replace('0.8', '1') ||
            'rgba(107, 114, 128, 1)',
          borderWidth: 1,
        });
      });
    }

    return {
      type: 'bar',
      data: {
        labels: chartData.map((row) => {
          const label = String(row[labelColumn] || 'Sin etiqueta');
          return label.length > 15 ? label.substring(0, 15) + '...' : label;
        }),
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: datasets.length > 1,
            labels: {
              usePointStyle: true,
              padding: 10,
            },
          },
          tooltip: {
            callbacks: {
              label: function (context: any) {
                const value = context.parsed?.y || context.parsed || 0;
                return `${context.dataset.label}: ${value.toLocaleString()}`;
              },
              title: function (context: any) {
                if (
                  context &&
                  context.length > 0 &&
                  context[0].dataIndex < chartData.length
                ) {
                  return String(
                    chartData[context[0].dataIndex][labelColumn] ||
                      'Sin etiqueta',
                  );
                }
                return '';
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value: any) {
                return Number(value).toLocaleString();
              },
            },
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
      },
    };
  }

  private createPieChart(
    data: any[],
    columns: string[],
    columnMapping: {
      labelColumn: string;
      valueColumn: string;
      additionalColumns?: string[];
    },
  ): ChartConfiguration {
    const { labelColumn, valueColumn } = columnMapping;

    console.log(
      '🥧 Pie Chart - Label Column:',
      labelColumn,
      'Value Column:',
      valueColumn,
    );

    const sortedData = data
      .map((row) => ({ ...row, numericValue: Number(row[valueColumn]) || 0 }))
      .filter((row) => row.numericValue > 0)
      .sort((a, b) => b.numericValue - a.numericValue)
      .slice(0, 10);

    if (sortedData.length === 0) {
      return this.createBarChart(data, columns, columnMapping);
    }

    return {
      type: 'pie',
      data: {
        labels: sortedData.map((row) =>
          String(row[labelColumn] || 'Sin etiqueta'),
        ),
        datasets: [
          {
            data: sortedData.map((row) => row.numericValue),
            backgroundColor: [
              'rgba(195, 0, 47, 0.8)', // Lala Red
              'rgba(59, 130, 246, 0.8)', // Blue
              'rgba(16, 185, 129, 0.8)', // Green
              'rgba(245, 101, 101, 0.8)', // Light Red
              'rgba(251, 191, 36, 0.8)', // Yellow
              'rgba(139, 92, 246, 0.8)', // Purple
              'rgba(236, 72, 153, 0.8)', // Pink
              'rgba(34, 197, 94, 0.8)', // Emerald
              'rgba(249, 115, 22, 0.8)', // Orange
              'rgba(107, 114, 128, 0.8)', // Gray
            ],
            borderWidth: 1,
            borderColor: '#ffffff',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              usePointStyle: true,
              padding: 10,
              font: { size: 10 },
            },
          },
          tooltip: {
            callbacks: {
              label: function (context: any) {
                const value = context.parsed || 0;
                const dataset = context.dataset;
                const total = dataset.data.reduce(
                  (a: number, b: number) => (a || 0) + (b || 0),
                  0,
                );
                const percentage =
                  total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
              },
            },
          },
        },
      },
    };
  }

  private createLineChart(
    data: any[],
    columns: string[],
    columnMapping: {
      labelColumn: string;
      valueColumn: string;
      additionalColumns?: string[];
    },
  ): ChartConfiguration {
    const { labelColumn, valueColumn, additionalColumns } = columnMapping;

    console.log(
      '📈 Line Chart - Label Column:',
      labelColumn,
      'Value Column:',
      valueColumn,
    );

    const datasets = [
      {
        label: valueColumn,
        data: data.map((row) => Number(row[valueColumn]) || 0),
        borderColor: 'rgba(195, 0, 47, 1)', // Lala Red
        backgroundColor: 'rgba(195, 0, 47, 0.1)',
        tension: 0.1,
        pointBackgroundColor: 'rgba(195, 0, 47, 1)',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ];

    // Agregar líneas adicionales
    if (additionalColumns && additionalColumns.length > 0) {
      const additionalColors = [
        'rgba(59, 130, 246, 1)', // Blue
        'rgba(16, 185, 129, 1)', // Green
      ];

      additionalColumns.forEach((col, index) => {
        datasets.push({
          label: col,
          data: data.map((row) => Number(row[col]) || 0),
          borderColor: additionalColors[index] || 'rgba(107, 114, 128, 1)',
          backgroundColor:
            additionalColors[index]?.replace('1)', '0.1)') ||
            'rgba(107, 114, 128, 0.1)',
          tension: 0.1,
          pointBackgroundColor:
            additionalColors[index] || 'rgba(107, 114, 128, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        });
      });
    }

    return {
      type: 'line',
      data: {
        labels: data.map((row) => String(row[labelColumn] || 'Sin etiqueta')),
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: datasets.length > 1,
            labels: {
              usePointStyle: true,
              padding: 10,
            },
          },
          tooltip: {
            callbacks: {
              label: function (context: any) {
                const value = context.parsed?.y || context.parsed || 0;
                return `${context.dataset.label}: ${value.toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value: any) {
                return Number(value).toLocaleString();
              },
            },
          },
        },
      },
    };
  }

  formatCellValue(value: any): string {
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'number') {
      return value.toLocaleString();
    }

    return value.toString();
  }

  clearChat() {
    if (confirm('¿Estás seguro de que quieres limpiar el historial de chat?')) {
      this.charts.forEach((chart) => chart.destroy());
      this.charts = [];

      this.apiService.clearChatHistory();
      this.sqlVisible = [];
      this.chartVisible = [];
      this.viewMode = [];
      this.contextVisible = [];
      this.citationsExpanded = [];
    }
  }

  // Método para verificar si un mensaje está en estado de loading
  isMessageLoading(messageIndex: number): boolean {
    // Solo mostrar loading si:
    // 1. Está en estado de loading general
    // 2. Es el último mensaje
    // 3. El mensaje no tiene interpretation ni error
    const message = this.chatHistory.messages[messageIndex];
    const isLastMessage = messageIndex === this.chatHistory.messages.length - 1;

    return (
      this.isLoading &&
      isLastMessage &&
      !message.interpretation &&
      !message.answer &&
      message.success !== false
    );
  }

  // Determina si debe mostrarse la burbuja del bot (evita burbujas vacías)
  shouldShowBotBubble(message: ChatMessage, index: number): boolean {
    return (
      this.isMessageLoading(index) ||
      (message.needs_clarification && !this.isMessageLoading(index)) ||
      this.hasRAGResponse(message) ||
      (this.hasValidResponse(message) && !this.hasRAGResponse(message)) ||
      this.isMessageError(message)
    );
  }

  // ✅ MEJORADO: Mejor detección de errores
  isMessageError(message: ChatMessage): boolean {
    return (
      message.success === false ||
      (message.error_details !== undefined && message.error_details !== null)
    );
  }

  // Método para verificar si un mensaje tiene respuesta válida
  hasValidResponse(message: ChatMessage): boolean {
    return (
      !message.needs_clarification &&
      !!message.interpretation &&
      message.success !== false &&
      !this.isMessageLoading(this.chatHistory.messages.indexOf(message))
    );
  }

  // 🚀 NUEVO: Método para obtener el mensaje completo incluyendo datos de "no resultados"
  getCompleteInterpretation(message: ChatMessage): string {
    if (!message.interpretation) {
      return '';
    }

    let completeMessage = message.interpretation;

    // Verificar si hay datos de "no resultados" y concatenarlos
    if (
      message.query_results &&
      message.query_results.data &&
      message.query_results.data.length > 0 &&
      message.query_results.row_count === 0
    ) {
      const noResultsData = message.query_results.data[0];

      if (noResultsData.mensaje) {
        completeMessage += `\n\n📋 ${noResultsData.mensaje}`;
      }

      if (noResultsData.sugerencia) {
        completeMessage += `\n\n💡 Sugerencia: ${noResultsData.sugerencia}`;
      }
    }

    return completeMessage;
  }

  // 🚀 NUEVO: Verificar si es un mensaje de "no resultados"
  isNoResultsMessage(message: ChatMessage): boolean {
    return !!(
      message.query_results &&
      message.query_results.row_count === 0 &&
      message.query_results.data &&
      message.query_results.data.length > 0 &&
      message.query_results.data[0].mensaje
    );
  }

  // Método para verificar si los datos son apropiados para gráficos
  canShowChart(message: ChatMessage): boolean {
    return !!(
      message.query_results &&
      message.query_results.data &&
      message.query_results.data.length > 0 &&
      message.query_results.columns.length >= 2 &&
      message.query_results.row_count > 0 &&
      !this.isNoResultsMessage(message)
    );
  }

  // 🚀 NUEVO: Verificar si hay contexto aplicado para mostrar - CON VALIDACIONES SEGURAS
  hasContextApplied(message: ChatMessage): boolean {
    if (!message.context_applied) {
      return false;
    }

    return !!(
      message.context_applied.temporal ||
      message.context_applied.auto_mapped_brands ||
      message.context_applied.correction
    );
  }

  // 🚀 NUEVO: Verificar si el mensaje tiene respuesta RAG válida
  hasRAGResponse(message: ChatMessage): boolean {
    return !!(message.answer && message.answer.trim().length > 0);
  }

  // Verificar si el mensaje tiene detalles de política (responsable/archivo)
  hasPolicyDetails(message: ChatMessage): boolean {
    return !!(
      message.policy_details &&
      message.policy_details.length > 0 &&
      message.policy_details.some((d) => d.responsable || d.archivo)
    );
  }

  // 🚀 NUEVO: Verificar si el mensaje tiene citaciones
  hasCitations(message: ChatMessage): boolean {
    return !!(message.citations && message.citations.length > 0);
  }

  // 🚀 NUEVO: Toggle para expandir/colapsar citaciones
  toggleCitationsVisibility(index: number): void {
    this.citationsExpanded[index] = !this.citationsExpanded[index];
  }

  // 🚀 NUEVO: Formatear citación para mostrar
  formatCitation(citation: Citation): string {
    return `[Fuente: ${citation.policy_name}, Sección ${citation.section}]`;
  }

  // 🚀 NUEVO: Renderizar markdown básico en respuestas
  renderMarkdown(text: string): string {
    if (!text) return '';

    let rendered = text;

    // Extraer links markdown antes de escapar HTML para preservarlos
    const linkPlaceholders: string[] = [];
    rendered = rendered.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      (_match, text, url) => {
        const idx = linkPlaceholders.length;
        linkPlaceholders.push(
          `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${text}</a>`,
        );
        return `%%LINK_${idx}%%`;
      },
    );

    // Convertir URLs sueltas (no dentro de links markdown) a links clickeables
    rendered = rendered.replace(/(https?:\/\/[^\s<>\[\]()]+)/g, (url) => {
      const idx = linkPlaceholders.length;
      linkPlaceholders.push(
        `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${url}</a>`,
      );
      return `%%LINK_${idx}%%`;
    });

    // Escapar HTML para seguridad
    rendered = rendered
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Restaurar links desde placeholders
    linkPlaceholders.forEach((link, idx) => {
      rendered = rendered.replace(`%%LINK_${idx}%%`, link);
    });

    // Bold: **text** o __text__
    rendered = rendered.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    rendered = rendered.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* o _text_
    rendered = rendered.replace(/\*(.+?)\*/g, '<em>$1</em>');
    rendered = rendered.replace(/_(.+?)_/g, '<em>$1</em>');

    // Code inline: `code`
    rendered = rendered.replace(
      /`(.+?)`/g,
      '<code class="bg-gray-200 px-1 rounded text-sm">$1</code>',
    );

    // Line breaks
    rendered = rendered.replace(/\n/g, '<br>');

    // Listas con viñetas: - item o * item
    rendered = rendered.replace(/^[-*]\s+(.+)$/gm, '<li class="ml-4">$1</li>');

    // Listas numeradas: 1. item
    rendered = rendered.replace(
      /^\d+\.\s+(.+)$/gm,
      '<li class="ml-4 list-decimal">$1</li>',
    );

    // Destacar citaciones en el texto [Fuente: ...]
    rendered = rendered.replace(
      /\[Fuente:\s*([^\]]+)\]/g,
      '<span class="citation-inline bg-blue-100 text-blue-800 px-1 rounded text-sm font-medium">[Fuente: $1]</span>',
    );

    return rendered;
  }

  // 🚀 NUEVO: Obtener clase de relevancia para citación
  getCitationRelevanceClass(citation: Citation): string {
    if (!citation.relevance_score) return 'bg-gray-100';

    if (citation.relevance_score >= 0.8) return 'bg-green-100 border-green-300';
    if (citation.relevance_score >= 0.6)
      return 'bg-yellow-100 border-yellow-300';
    return 'bg-orange-100 border-orange-300';
  }

  // 🚀 NUEVO: Obtener texto de relevancia
  getRelevanceText(score: number | undefined): string {
    if (!score) return '';

    if (score >= 0.8) return 'Alta relevancia';
    if (score >= 0.6) return 'Relevancia media';
    return 'Baja relevancia';
  }

  // 🚀 NUEVO: Obtener badge de tipo de consulta - CON VALIDACIONES SEGURAS
  getQueryTypeBadge(message: ChatMessage): string {
    if (!message.query_analysis?.query_type) {
      return '';
    }

    const typeMap: { [key: string]: string } = {
      historical_trend: '📈 Tendencia Histórica',
      growth_analysis: '📊 Análisis de Crecimiento',
      ranking: '🏆 Ranking',
      market_share: '📈 Participación de Mercado',
      comparative: '⚔️ Comparativo',
      segment_analysis: '🏷️ Análisis por Segmento',
      volume: '🚗 Volumen de Ventas',
      general: '📋 Consulta General',
    };

    return typeMap[message.query_analysis.query_type] || '';
  }

  // 🚀 NUEVO: Obtener badge de complejidad - CON VALIDACIONES SEGURAS
  getComplexityBadge(message: ChatMessage): string {
    if (!message.query_analysis?.complexity_level) {
      return '';
    }

    const complexityMap: { [key: string]: string } = {
      simple: '🟢 Simple',
      medium: '🟡 Medio',
      complex: '🔴 Complejo',
      advanced: '🟣 Avanzado',
    };

    return complexityMap[message.query_analysis.complexity_level] || '';
  }

  // 🚀 NUEVO: Auto-resize del textarea
  autoResize(event: any): void {
    const textarea = event.target;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Set the height based on scrollHeight, with min and max constraints
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 24), 300);
    textarea.style.height = newHeight + 'px';
  }

  // 🚀 NUEVO: Reset textarea height to normal size
  resetTextareaHeight(): void {
    if (this.textareaRef && this.textareaRef.nativeElement) {
      const textarea = this.textareaRef.nativeElement;
      textarea.style.height = '24px'; // Reset to minimum height
    }
  }
}
